/**
 * @file SecurityRoleAttributeGroup.js
 * @description Renders a bordered category card containing attribute group rows with No Access / Read Only / Full Access radio selections.
 */
import React from 'react';
import styles from './security-roles.module.scss';
import { SecurityAttributes } from '../../utils/data/enums';
import { getText } from '../../i18n';

const LEVEL_NO_ACCESS = 0;
const LEVEL_READ_ONLY = 1;
const LEVEL_FULL_ACCESS = 2;

const SecurityRoleAttributeGroup = ({
    category,
    groups,
    attributesByGroup,
    groupSelections,
    onLevelChange,
    disabledGroups,
    dependentGroupIds,
    readOnly,
}) => (
    <div className={styles.categoryCard}>
        {category?.name && (
            <div className={styles.categoryHeader}>{category.name}</div>
        )}
        {groups.map(group => {
            const attributes = attributesByGroup[group.id] ?? [];
            const hasReadOnly = attributes.some(
                attr => attr.securityAttributeLevelId === LEVEL_READ_ONLY,
            );
            const hasFullAccess = attributes.some(
                attr => attr.securityAttributeLevelId === LEVEL_FULL_ACCESS,
            );
            const selectedLevel = groupSelections[group.id] ?? LEVEL_NO_ACCESS;
            const isDisabled =
                readOnly || (disabledGroups ?? []).includes(group.id);

            return (
                <div
                    key={group.id}
                    className={`${styles.groupRow} ${isDisabled ? styles.groupRowDisabled : ''}`}
                >
                    <div className={`${styles.categoryTitle} mb-1`}>
                        {group.name}
                    </div>
                    <div className={`${styles.categorySubtitle} mb-2`}>
                        {`Set Security on ${group.name}`}
                    </div>

                    <div>
                        <div className="form-check form-check-inline">
                            <input
                                type="radio"
                                className="form-check-input"
                                id={`group-${group.id}-no-access`}
                                name={`group-${group.id}`}
                                value={LEVEL_NO_ACCESS}
                                checked={selectedLevel === LEVEL_NO_ACCESS}
                                disabled={isDisabled}
                                onChange={() =>
                                    onLevelChange(group.id, LEVEL_NO_ACCESS)
                                }
                            />
                            <label
                                className="form-check-label"
                                htmlFor={`group-${group.id}-no-access`}
                            >
                                {getText('msg_a3141407d0cb')}
                            </label>
                        </div>

                        {hasReadOnly && (
                            <div className="form-check form-check-inline">
                                <input
                                    type="radio"
                                    className="form-check-input"
                                    id={`group-${group.id}-read-only`}
                                    name={`group-${group.id}`}
                                    value={LEVEL_READ_ONLY}
                                    checked={selectedLevel === LEVEL_READ_ONLY}
                                    disabled={isDisabled}
                                    onChange={() =>
                                        onLevelChange(group.id, LEVEL_READ_ONLY)
                                    }
                                />
                                <label
                                    className="form-check-label"
                                    htmlFor={`group-${group.id}-read-only`}
                                >
                                    {attributes.some(
                                        attr =>
                                            attr.id ===
                                            SecurityAttributes.ReportsAccess,
                                    )
                                        ? 'Access'
                                        : 'Read Only'}
                                </label>
                            </div>
                        )}

                        {hasFullAccess && (
                            <div className="form-check form-check-inline">
                                <input
                                    type="radio"
                                    className="form-check-input"
                                    id={`group-${group.id}-full-access`}
                                    name={`group-${group.id}`}
                                    value={LEVEL_FULL_ACCESS}
                                    checked={selectedLevel === LEVEL_FULL_ACCESS}
                                    disabled={isDisabled}
                                    onChange={() =>
                                        onLevelChange(group.id, LEVEL_FULL_ACCESS)
                                    }
                                />
                                <label
                                    className="form-check-label"
                                    htmlFor={`group-${group.id}-full-access`}
                                >
                                    {getText('msg_b0b0baa36b8f')}
                                </label>
                            </div>
                        )}
                    </div>
                </div>
            );
        })}
    </div>
);

export default SecurityRoleAttributeGroup;
